import { HTTP_INTERCEPTORS, HttpRequest, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarConfig, MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { OktaAuth, OktaAuthOptions, StorageType } from '@okta/okta-auth-js';
import {
	NgHttpCachingModule,
	NgHttpCachingConfig,
	NgHttpCachingStrategy,
	NgHttpCachingLocalStorage
} from 'ng-http-caching';
import { provideNgxWebstorage, withSessionStorage } from 'ngx-webstorage';

import {
	CdmDeviceReadonlyStateProvider,
	provideCdmDeviceUiApiService
} from '@shure/cloud/device-management/device-ui/data-access';
import { DevicesDataAccessModule, SystemService } from '@shure/cloud/device-management/devices/data-access';
import { CdmAnalyticsFacade } from '@shure/cloud/device-management/shared/analytics';
import {
	Ad4dCdmDeviceBehaviorPlugin,
	Ad4qCdmDeviceBehaviorPlugin,
	Ad600CdmDeviceBehaviorPlugin,
	Ad610CdmDeviceBehaviorPlugin,
	AdtdCdmDeviceBehaviorPlugin,
	AdtqCdmDeviceBehaviorPlugin,
	AniusbCdmDeviceBehaviorPlugin,
	Dca901CdmDeviceBehaviorPlugin,
	DefaultCdmDeviceBehaviorPlugin,
	ImxbpCdmDeviceBehaviorPlugin,
	ImxComputeCdmDeviceBehaviorPlugin,
	ImxComputeZoomCdmDeviceBehaviorPlugin,
	ImxRoomCdmDeviceBehaviorPlugin,
	Imxtp10CdmDeviceBehaviorPlugin,
	Mxa310CdmDeviceBehaviorPlugin,
	Mxa320CdmDeviceBehaviorPlugin,
	Mxa710CdmDeviceBehaviorPlugin,
	Mxa720CdmDeviceBehaviorPlugin,
	Mxa901CdmDeviceBehaviorPlugin,
	Mxa902CdmDeviceBehaviorPlugin,
	Mxa910CdmDeviceBehaviorPlugin,
	Mxa920CdmDeviceBehaviorPlugin,
	Mxa925CdmDeviceBehaviorPlugin,
	MxaMuteCdmDeviceBehaviorPlugin,
	MxcwaptCdmDeviceBehaviorPlugin,
	Mxn5CdmDeviceBehaviorPlugin,
	Mxn6CdmDeviceBehaviorPlugin,
	MxnAmpCdmDeviceBehaviorPlugin,
	MxwApxd2CdmDeviceBehaviorPlugin,
	P100CdmDeviceBehaviorPlugin,
	P300CdmDeviceBehaviorPlugin,
	SbcCdmDeviceBehaviorPlugin
} from '@shure/cloud/device-management/shared/device-behavior';
import { SdcSupportService } from '@shure/cloud/device-management/shared/services';
import { ApolloSysApiCloudModule } from '@shure/cloud/shared/apollo';
import { ChatAssistantApiModule } from '@shure/cloud/shared/chat-assistant/data-access';
import { CloudDeviceBehaviorPluginsModule } from '@shure/cloud/shared/device-behavior';
import { FeatureRequestApiModule } from '@shure/cloud/shared/feature-request/data-access';
import { UserAgGridPreferencesService } from '@shure/cloud/shared/feature-user-app-preferences';
import { AppEnvironmentGuard } from '@shure/cloud/shared/guards/app-environment-guard';
import { MaintenanceApiModule } from '@shure/cloud/shared/maintenance/data-access';
import { provideMaintenanceModeInitializer } from '@shure/cloud/shared/maintenance/utils';
import { NotificationsBellIconMainComponent } from '@shure/cloud/shared/notifications/feature-notifications-bell-icon';
import { NotificationsDataAccessApiModule } from '@shure/cloud/shared/notifications/notifications-data-access';
import { OktaInterfaceService, OktaModule } from '@shure/cloud/shared/okta/data-access';
import { provideOrganizationCloud } from '@shure/cloud/shared/organization-switcher/feature-organization-switcher';
import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import { HeaderToolbarModule } from '@shure/cloud/shared/ui/components';
import { UsersApiModule } from '@shure/cloud/shared/users/data-access';
import { MixpanelAnalyticsProvider } from '@shure/cloud/shared/utils/analytics';
import { AnalyticsService } from '@shure/cloud/shared/utils/analytics';
import { APP_ENVIRONMENT, BELL_ICON_COMPONENT_TOKEN, getShureCloudUri } from '@shure/cloud/shared/utils/config';
import { CookiebotService } from '@shure/cloud/shared/utils/cookiebot';
import { FEATURE_FLAG_SERVICE, initializeFeatureFlagsService } from '@shure/cloud/shared/utils/feature-flags';
import { ApiBaseurlInterceptor, ApiUrlConfig, AuthTokenInterceptor, HttpModule } from '@shure/cloud/shared/utils/http';
import { VerificationCodeApiModule } from '@shure/cloud/shared/verification-code/data-access';
import { provideDisabledDeviceUiFeatures } from '@shure/device-ui/shared/feature-toggle';
import { PRISM_SNACKBAR_CONFIG, PrismBannerComponent, PrismSnackbarComponent } from '@shure/prism-angular-components';
import { SysApiDeviceAuthenticationCoreDataAccessModule } from '@shure/shared/angular/data-access/device-authentication';
import { DeviceDiscoveryDataAccessModule } from '@shure/shared/angular/data-access/device-discovery';
import { MeteringApiService } from '@shure/shared/angular/data-access/device-metering';
import { DeviceBehaviorPluginsModule } from '@shure/shared/angular/device-behavior/core';
import { DeviceAuthenticationDataAccessModule } from '@shure/shared/angular/features/device-authentication/data-access';
import {
	LightsDataAccessModule,
	provideLightsSettingsExcludeMicrophoneAngle
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import {
	DeviceLightsBehaviorPluginService,
	provideLightsImageProvider
} from '@shure/shared/angular/features/device-lights-settings/feature-device-image-next';
import { provideRoomAdaptDataAccessNoop } from '@shure/shared/angular/features/room-adapt/data-access';
import { SpectrumScannerDataAccessModule } from '@shure/shared/angular/features/spectrum-scanner/data-access';
import { AnalyticsModule } from '@shure/shared/angular/utils/analytics';
import { provideDeviceReadonlyState } from '@shure/shared/angular/utils/device-readonly';
import { StatsigProvider, provideStatsigFeatureFlags } from '@shure/shared/angular/utils/feature-flags/statsig';
import {
	TranslocoRootModule,
	provideTranslationScopeLoaderInitializer,
	provideTranslocoInitializer
} from '@shure/shared/angular/utils/i18n';
import { DEFAULT_APP_LANGUAGES } from '@shure/shared/angular/utils/i18n-app-defaults';
import { ConsoleLoggerSink, LoggingModule, StaticLoggingConfigProvider } from '@shure/shared/angular/utils/logging';

import { environment } from '../environments/environment';
import { MaterialIconsModule } from '../modules/material-icons.module';
import { CdmMeteringApiService } from '../services/cdm-metering-api.service';
import { CloudSystemService } from '../services/system';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

const appBaseURL = window.location.origin;
const shureCloudUriMap = environment.shureCloudUriMap;

const oktaDomain =
	appBaseURL.includes('services') || appBaseURL.includes('localhost')
		? environment.authorization.oktaDomain
		: environment.authorization.oktaDomainExt;

const storageTypes: StorageType[] = ['sessionStorage'];

const oktaConfig: OktaAuthOptions = {
	issuer: 'https://' + oktaDomain + environment.authorization.authServerPath,
	clientId: environment.authorization.clientId,
	redirectUri: `${appBaseURL}/signin/callback`,
	scopes: environment.authorization.scopes,
	logoutUrl: getShureCloudUri(appBaseURL, shureCloudUriMap),
	postLogoutRedirectUri: getShureCloudUri(appBaseURL, shureCloudUriMap),
	pkce: true,
	tokenManager: {
		autoRenew: true,
		syncStorage: false // since we're using session storage, don't sync across tabs.
	},
	storageManager: {
		token: {
			storageTypes: storageTypes
		},
		cache: {
			storageTypes: storageTypes
		},
		transaction: {
			storageTypes: storageTypes
		}
	}
};
const oktaAuth = new OktaAuth(oktaConfig);

const apiUrlConfig: ApiUrlConfig = {
	apiBaseUrl: environment.orgs?.orgsApiUrl,
	rolesApiUrl: environment.orgs?.rolesApiUrl
};

const cacheApiUrls = ['/permissions', '/version', '/contactdetails', '/organizationIds', '/users/api/v1/all'];
const ngHttpCachingConfig: NgHttpCachingConfig = {
	lifetime: 1000 * 360, // cache expire after 6 mins
	allowedMethod: ['GET'],
	cacheStrategy: NgHttpCachingStrategy.ALLOW_ALL,
	store: new NgHttpCachingLocalStorage(),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	isCacheable: (req: HttpRequest<any>): boolean | undefined => {
		//Caching the response of the cacheApiUrls
		for (const url of cacheApiUrls) {
			if (req.urlWithParams.indexOf(url) !== -1) {
				return true;
			}
		}
		// by returning "false" for all the other APIs
		return false;
	}
};

// TODO
// - Logging Sink

@NgModule({
	declarations: [AppComponent],
	bootstrap: [AppComponent],
	imports: [
		BrowserAnimationsModule,
		FormsModule,
		MatSidenavModule,
		MaterialIconsModule,
		TranslocoRootModule.forRoot(DEFAULT_APP_LANGUAGES, false, environment.appVersion),
		ReactiveFormsModule,
		OktaModule.forRoot(oktaAuth),
		NgIdleKeepaliveModule.forRoot(),
		ApolloSysApiCloudModule.forRoot(),
		DevicesDataAccessModule,
		AppRoutingModule,
		PrismBannerComponent,
		PrismSnackbarComponent,
		LoggingModule.forRoot(
			environment.production ? [ConsoleLoggerSink] : [ConsoleLoggerSink],
			{ base: { default: environment.logLevel } },
			StaticLoggingConfigProvider
		),
		CloudDeviceBehaviorPluginsModule,
		HeaderToolbarModule,
		NgHttpCachingModule.forRoot(ngHttpCachingConfig),
		MaintenanceApiModule.forRoot({ rootUrl: environment.orgs?.maintenanceApiUrl }),
		FeatureRequestApiModule.forRoot({ rootUrl: environment.orgs?.featureRequestApiUrl }),
		VerificationCodeApiModule.forRoot({ rootUrl: environment.orgs?.verificationCodeApiUrl }),
		HttpModule.forRoot(apiUrlConfig, [
			{
				provide: HTTP_INTERCEPTORS,
				useClass: ApiBaseurlInterceptor,
				multi: true
			},
			{
				provide: HTTP_INTERCEPTORS,
				useClass: AuthTokenInterceptor,
				multi: true
			}
		]),
		UsersApiModule.forRoot({
			rootUrl: environment.orgs?.usersApiUrl
		}),
		NotificationsDataAccessApiModule.forRoot({
			rootUrl: environment.orgs?.notificationsApiUrl
		}),
		AnalyticsModule.forRoot(MixpanelAnalyticsProvider),
		DeviceBehaviorPluginsModule.forRoot(
			[
				Ad4dCdmDeviceBehaviorPlugin,
				Ad4qCdmDeviceBehaviorPlugin,
				Ad600CdmDeviceBehaviorPlugin,
				Ad610CdmDeviceBehaviorPlugin,
				AdtdCdmDeviceBehaviorPlugin,
				AdtqCdmDeviceBehaviorPlugin,
				AniusbCdmDeviceBehaviorPlugin,
				Dca901CdmDeviceBehaviorPlugin,
				ImxComputeCdmDeviceBehaviorPlugin,
				ImxComputeZoomCdmDeviceBehaviorPlugin,
				ImxRoomCdmDeviceBehaviorPlugin,
				ImxbpCdmDeviceBehaviorPlugin,
				Imxtp10CdmDeviceBehaviorPlugin,
				Mxa310CdmDeviceBehaviorPlugin,
				Mxa320CdmDeviceBehaviorPlugin,
				Mxa710CdmDeviceBehaviorPlugin,
				Mxa720CdmDeviceBehaviorPlugin,
				Mxa901CdmDeviceBehaviorPlugin,
				Mxa902CdmDeviceBehaviorPlugin,
				Mxa910CdmDeviceBehaviorPlugin,
				Mxa920CdmDeviceBehaviorPlugin,
				Mxa925CdmDeviceBehaviorPlugin,
				MxaMuteCdmDeviceBehaviorPlugin,
				MxcwaptCdmDeviceBehaviorPlugin,
				Mxn5CdmDeviceBehaviorPlugin,
				Mxn6CdmDeviceBehaviorPlugin,
				MxnAmpCdmDeviceBehaviorPlugin,
				MxwApxd2CdmDeviceBehaviorPlugin,
				P100CdmDeviceBehaviorPlugin,
				P300CdmDeviceBehaviorPlugin,
				SbcCdmDeviceBehaviorPlugin
			],
			DefaultCdmDeviceBehaviorPlugin
		),
		DeviceDiscoveryDataAccessModule,
		LightsDataAccessModule,
		SpectrumScannerDataAccessModule,
		ChatAssistantApiModule.forRoot({ rootUrl: environment.orgs?.chatAssistantApiUrl }),

		// Required for auth/permissions related features in SDCs
		SysApiDeviceAuthenticationCoreDataAccessModule,
		DeviceAuthenticationDataAccessModule
	],
	providers: [
		provideCdmDeviceUiApiService(),
		provideDisabledDeviceUiFeatures([
			'metering',
			'settings.audio-encryption',
			'settings.factory-reset.debug-mode',
			'settings.factory-reset.password-confirmation',
			'settings.general.event-log-export',
			'settings.permissions',
			'settings.services.cloud-claim-status',
			'settings.wireless-mics.rf-spectrum-scanner'
		]),
		provideHttpClient(withInterceptorsFromDi()),
		provideNgxWebstorage(withSessionStorage()),
		provideTranslocoInitializer(),
		provideTranslocoMessageformat(),
		provideTranslationScopeLoaderInitializer(),
		provideMaintenanceModeInitializer(),
		provideRoomAdaptDataAccessNoop(),
		provideLightsImageProvider(DeviceLightsBehaviorPluginService),
		provideLightsSettingsExcludeMicrophoneAngle(),
		{ provide: OktaInterfaceService },
		{ provide: OrganizationsStoreService },
		{ provide: MeteringApiService, useClass: CdmMeteringApiService },
		{ provide: SdcSupportService },
		{ provide: APP_ENVIRONMENT, useValue: environment },
		{
			provide: MAT_RIPPLE_GLOBAL_OPTIONS,
			useValue: {
				disabled: true
			} satisfies RippleGlobalOptions
		},
		{
			provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
			useValue: {
				duration: 8000,
				verticalPosition: 'bottom',
				horizontalPosition: 'center',
				panelClass: ['mat-toolbar', 'mat-primary']
			} satisfies MatSnackBarConfig
		},
		{
			// eslint-disable-next-line max-len
			// http://shure-prism-web-components.dev-aws-pr-ms-influx.shure.com/?path=/docs/components-snackbar-snackbar-in-an-angular-app-prism-snackbar-service--docs
			provide: PRISM_SNACKBAR_CONFIG,
			useValue: {
				duration: 8000
			}
		},
		{ provide: BELL_ICON_COMPONENT_TOKEN, useValue: NotificationsBellIconMainComponent },
		AppEnvironmentGuard,
		{
			provide: SystemService,
			useClass: CloudSystemService
		},
		CookiebotService,
		{
			// Required for shared ShureCloud components that rely on the Cloud analytics service
			provide: AnalyticsService,
			useClass: CdmAnalyticsFacade
		},
		provideStatsigFeatureFlags({
			clientKey: environment.statsigConfig?.clientKey ?? '',
			tier: environment.statsigConfig?.tier ?? '',
			proxyApi: environment.statsigConfig?.proxyApi
		}),
		{
			provide: FEATURE_FLAG_SERVICE,
			useFactory: initializeFeatureFlagsService,
			deps: [StatsigProvider]
		},
		UserAgGridPreferencesService,
		provideDeviceReadonlyState(CdmDeviceReadonlyStateProvider),
		provideOrganizationCloud()
	]
})
export class AppModule {}
